import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, Unlock, Search, Presentation, GraduationCap, ShieldCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { ConfirmDialog, EmptyState } from "../../components/common";
import { UserFormDialog } from "../../components/UserFormDialog";
import { api, formatApiError } from "../../lib/api";
import { fmtDate, todayISO } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

function accessInfo(u) {
  if (u.user_type !== "eleve") return { label: "Permanent", cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300" };
  const t = todayISO();
  if (u.access_end && u.access_end < t) return { label: `Expiré le ${fmtDate(u.access_end)}`, cls: "bg-red-500/10 text-red-700 dark:text-red-300" };
  if (u.access_start && u.access_start > t) return { label: `À partir du ${fmtDate(u.access_start)}`, cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300" };
  return { label: `Jusqu'au ${fmtDate(u.access_end)}`, cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" };
}

export default function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preset, setPreset] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetSend, setResetSend] = useState(true);
  const [resetResult, setResetResult] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const list = (users || [])
    .filter((u) => filter === "all" || (filter === "eleve" ? u.user_type === "eleve" : u.user_type !== "eleve"))
    .filter((u) => `${u.prenom} ${u.nom} ${u.matricule}`.toLowerCase().includes(q.trim().toLowerCase()));

  const open = (u, p) => { setEditing(u); setPreset(p || null); setFormOpen(true); };

  const remove = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Accès supprimé");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const unlock = async (u) => {
    await api.post(`/users/${u.id}/unlock`);
    toast.success("Compte débloqué");
    load();
  };
  const doReset = async () => {
    try {
      const { data } = await api.post(`/users/${resetUser.id}/reset-pin`, { send_email: resetSend && !!resetUser.email });
      setResetResult(data);
      if (data.email_status) {
        if (data.email_status.sent) toast.success("Nouveau PIN envoyé par e-mail");
        else toast.warning(`E-mail non envoyé : ${data.email_status.error}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const counts = { all: users?.length || 0, formateur: (users || []).filter((u) => u.user_type !== "eleve").length, eleve: (users || []).filter((u) => u.user_type === "eleve").length };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {[
            ["all", "Tous"], ["formateur", "Formateurs"], ["eleve", "Élèves"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={cn("rounded-full border px-3 py-1 text-sm", filter === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent")}>
              {l} <span className="opacity-70 tabular-nums">{counts[k]}</span>
            </button>
          ))}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..." className="pl-8 h-8 w-48" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => open(null, { user_type: "eleve" })} data-testid="admin-new-eleve"><GraduationCap size={15} /> Accès élève</Button>
          <Button onClick={() => open(null, { user_type: "formateur" })} data-testid="admin-new-formateur"><Plus size={15} /> Accès formateur</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        {users && list.length === 0 ? (
          <EmptyState title="Aucun utilisateur" />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Utilisateur</th>
                <th className="text-left font-medium px-4 py-2.5">Matricule</th>
                <th className="text-left font-medium px-4 py-2.5">Type</th>
                <th className="text-left font-medium px-4 py-2.5">Accès</th>
                <th className="text-left font-medium px-4 py-2.5">État</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.map((u) => {
                const a = accessInfo(u);
                const locked = u.locked_until && new Date(u.locked_until) > new Date();
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium">{u.prenom} {u.nom}</p>
                      <p className="text-xs text-muted-foreground">{u.email || u.poste}</p>
                    </td>
                    <td className="px-4 py-2.5"><code>{u.matricule}</code></td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5">
                        {u.user_type === "eleve" ? <GraduationCap size={14} /> : u.is_admin ? <ShieldCheck size={14} className="text-primary" /> : <Presentation size={14} />}
                        {u.user_type === "eleve" ? "Élève" : u.is_admin ? "Formateur admin" : "Formateur"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5"><span className={cn("rounded-full px-2 py-0.5 text-xs whitespace-nowrap", a.cls)}>{a.label}</span></td>
                    <td className="px-4 py-2.5 text-xs">
                      {u.active === false ? <span className="text-muted-foreground">Désactivé</span> : locked ? <span className="text-red-600">Bloqué</span> : <span className="text-emerald-600">Actif</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-0.5">
                        {locked && <Button variant="ghost" size="icon" className="h-8 w-8" title="Débloquer" onClick={() => unlock(u)}><Unlock size={15} /></Button>}
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Réinitialiser le PIN" onClick={() => { setResetUser(u); setResetResult(null); setResetSend(true); }}><KeyRound size={15} /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Modifier" onClick={() => open(u)} data-testid={`user-edit-${u.id}`}><Pencil size={15} /></Button>
                        {u.id !== me.id && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Supprimer" onClick={() => setToDelete(u)}><Trash2 size={15} /></Button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <UserFormDialog open={formOpen} onOpenChange={setFormOpen} initial={editing} preset={preset} onSaved={load} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Supprimer cet accès ?" description={toDelete ? `${toDelete.prenom} ${toDelete.nom} ne pourra plus se connecter. La fiche élève éventuelle est conservée.` : ""} confirmLabel="Supprimer" onConfirm={remove} />
      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Réinitialiser le code PIN</DialogTitle>
            <DialogDescription>{resetUser ? `${resetUser.prenom} ${resetUser.nom} — matricule ${resetUser.matricule}` : ""}</DialogDescription>
          </DialogHeader>
          {resetResult ? (
            <div className="rounded-xl border border-border bg-muted/40 p-4">
              <p className="text-sm text-muted-foreground">Nouveau code PIN (affiché une seule fois) :</p>
              <p className="font-mono text-2xl font-semibold tracking-widest mt-1">{resetResult.pin}</p>
            </div>
          ) : (
            <label className={cn("flex items-center gap-2 text-sm", !resetUser?.email && "opacity-50")}>
              <Checkbox checked={resetSend && !!resetUser?.email} disabled={!resetUser?.email} onCheckedChange={(v) => setResetSend(!!v)} />
              Envoyer le nouveau PIN par e-mail {resetUser?.email ? `(${resetUser.email})` : "(aucun e-mail)"}
            </label>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetUser(null)}>{resetResult ? "Fermer" : "Annuler"}</Button>
            {!resetResult && <Button onClick={doReset}>Générer un nouveau PIN</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
